import React, { useState, useRef, useEffect } from 'react';

// Toggle lazy loading on/off - Set to false to disable lazy loading
const ENABLE_LAZY_LOADING = false;

const LazyImage = ({ 
  src, 
  alt = '', 
  className = '', 
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvYWRpbmcuLi48L3RleHQ+PC9zdmc+',
  onLoad,
  onError,
  ...props 
}) => {
  // Always call hooks unconditionally (React rules requirement)
  const [imageSrc, setImageSrc] = useState(ENABLE_LAZY_LOADING ? placeholder : src);
  const [imageRef, setImageRef] = useState();
  const [isLoaded, setIsLoaded] = useState(!ENABLE_LAZY_LOADING); // Start as loaded if lazy loading is disabled
  const [hasError, setHasError] = useState(false);
  const observerRef = useRef();

  useEffect(() => {
    // Only set up Intersection Observer if lazy loading is enabled
    if (!ENABLE_LAZY_LOADING || !imageRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(src);
            observer.unobserve(imageRef);
          }
        });
      },
      {
        rootMargin: '50px 0px', // Start loading 50px before the image comes into view
        threshold: 0.01
      }
    );

    observer.observe(imageRef);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [imageRef, src]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setHasError(true);
    if (onError) onError();
  };

  // If lazy loading is disabled, render a regular img tag
  if (!ENABLE_LAZY_LOADING) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onLoad={onLoad}
        onError={onError}
        {...props}
      />
    );
  }

  // Lazy loading enabled - use Intersection Observer
  return (
    <img
      ref={setImageRef}
      src={imageSrc}
      alt={alt}
      className={`${className} ${isLoaded ? 'lazy-image-loaded' : 'lazy-image-loading'}`}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default LazyImage;
